import { Link } from "react-router-dom";
import { Droplets, Phone, MessageCircle, MapPin, Mail, Building2 } from "lucide-react";
import { COMPANY } from "../data/company";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-900 text-slate-300 pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-heading font-black text-lg text-white">
                Dete<span className="text-cyan-400">co</span>
                <span className="text-emerald-400">green</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              Magazin online de detergenți și balsamuri premium, fabricați în
              România. Curățenie impecabilă pentru întreaga familie.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">
              Contact
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-2.5 text-slate-300 hover:text-cyan-400 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="flex items-center gap-2.5 text-slate-300 hover:text-[#25d366] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp comenzi
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-2.5 text-slate-300 hover:text-cyan-400 transition-colors break-all"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  {COMPANY.email}
                </a>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-semibold"
                >
                  Pagina contact →
                </Link>
              </li>
            </ul>
          </div>

          {/* Informații */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">
              Informații
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/termeni-conditii" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Termeni și condiții
                </Link>
              </li>
              <li>
                <Link to="/politica-confidentialitate" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Politica de confidențialitate (GDPR)
                </Link>
              </li>
              <li>
                <Link to="/politica-cookies" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Politica de cookies
                </Link>
              </li>
              <li>
                <Link to="/politica-retur" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Politica de retur
                </Link>
              </li>
              <li>
                <Link to="/politica-livrare" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Politica de livrare
                </Link>
              </li>
              <li>
                <Link to="/politica-garantie" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Politica de garanție
                </Link>
              </li>
            </ul>
          </div>

          {/* Date firmă */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-4">
              Date firmă
            </h4>
            <ul className="space-y-2 text-xs text-slate-400 leading-relaxed">
              <li className="flex items-start gap-2">
                <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-500" />
                <span className="text-slate-300 font-semibold">
                  {COMPANY.legalName}
                </span>
              </li>
              <li>CUI: {COMPANY.cui}</li>
              <li>Reg. Comerț: {COMPANY.regCom}</li>
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-500" />
                <span>{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* ANPC logos */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://anpc.ro/ce-este-sal/"
                target="_blank"
                rel="noopener noreferrer"
                title="ANPC – Soluționarea Alternativă a Litigiilor"
                className="bg-white rounded-lg p-1.5 hover:opacity-90 transition-opacity"
                data-testid="anpc-sal-link"
              >
                <img
                  src="https://detergentidegresanti.ro/wp-content/uploads/2025/10/anpc-300x81-1.webp"
                  alt="ANPC – Soluționarea Alternativă a Litigiilor"
                  referrerPolicy="no-referrer"
                  className="h-12 w-auto"
                />
              </a>
              <a
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                rel="noopener noreferrer"
                title="SOL – Soluționarea Online a Litigiilor (UE)"
                className="bg-white rounded-lg p-1.5 hover:opacity-90 transition-opacity"
                data-testid="anpc-sol-link"
              >
                <img
                  src="https://detergentidegresanti.ro/wp-content/uploads/2025/10/sol-300x81-1.webp"
                  alt="SOL – Soluționarea Online a Litigiilor"
                  referrerPolicy="no-referrer"
                  className="h-12 w-auto"
                />
              </a>
            </div>

            <div className="text-xs text-slate-500 text-center md:text-right">
              <div>
                © {new Date().getFullYear()} {COMPANY.shortName}. Toate
                drepturile rezervate.
              </div>
              <div className="mt-1 text-slate-600">
                {COMPANY.legalName} · CUI {COMPANY.cui}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
